import moment from 'moment';
import {
	CALENDAR_DATE_FORMAT,
	VALIDITY_STATUS,
	daysUntil,
	getValidityStatus,
	isKnownInterval,
} from './ExpiryRule';

// rest-api status vocabulary (AddonDomainMapper.determine_product_addon_status). 'available' covers an
// add-on that never started and an expired trial; 'pending active' has not started yet.
export const ADDON_SERVER_STATUS = Object.freeze({
	TRIAL: 'trial',
	ACTIVE: 'active',
	PENDING_ACTIVE: 'pending active',
	EXPIRED: 'expired',
	AVAILABLE: 'available',
});

// Only an add-on that is licensed now (or was and lapsed) raises an alert or appears as a validity row.
const COUNTED_ADDON_STATUSES = Object.freeze([
	ADDON_SERVER_STATUS.TRIAL,
	ADDON_SERVER_STATUS.ACTIVE,
	ADDON_SERVER_STATUS.EXPIRED,
]);

const STATUS_RANK = Object.freeze({
	[VALIDITY_STATUS.EXPIRED]: 0,
	[VALIDITY_STATUS.EXPIRING]: 1,
	[VALIDITY_STATUS.ACTIVE]: 2,
});

// rest-api serialises naive server wall-clock datetimes labelled +00:00 (the rest-api container runs in UTC).
// Reading them as UTC keeps the calendar day the server meant, whatever the browser's time zone; the day is
// then compared with the browser's own calendar day.
export const toServerCalendarDate = (value) => {
	return value ? moment.utc(value).format(CALENDAR_DATE_FORMAT) : null;
};

export const getAddonValidity = (productAddon, interval) => {
	if (!COUNTED_ADDON_STATUSES.includes(productAddon.status)) {
		return null;
	}

	const calendarDate = toServerCalendarDate(productAddon.endDate);

	if (!calendarDate) {
		return null;
	}

	if (productAddon.status === ADDON_SERVER_STATUS.EXPIRED) {
		return { status: VALIDITY_STATUS.EXPIRED, calendarDate };
	}

	// The server called it active, so a browser clock a day ahead must not turn it into Expired.
	const daysLeft = Math.max(daysUntil(calendarDate), 0);

	return { status: getValidityStatus(daysLeft, interval), calendarDate };
};

// null means "unknown": the field is missing (older rest-api), not an array, or the company interval has not
// resolved yet. An empty array is a known "no add-ons".
export const getAddonRows = (productAddons, interval) => {
	if (!Array.isArray(productAddons) || !isKnownInterval(interval)) {
		return null;
	}

	return productAddons
		.map((productAddon) => {
			const validity = getAddonValidity(productAddon, interval);

			return validity ? { id: productAddon.id, name: productAddon.name, ...validity } : null;
		})
		.filter(Boolean)
		.sort((rowA, rowB) => {
			return STATUS_RANK[rowA.status] - STATUS_RANK[rowB.status]
				|| rowA.calendarDate.localeCompare(rowB.calendarDate)
				|| rowA.name.localeCompare(rowB.name);
		});
};

// null means "no alert", which covers a machine with healthy add-ons and a machine whose add-ons are unknown.
export const getAddonsAlert = (productAddons, interval) => {
	const rows = getAddonRows(productAddons, interval);

	if (!rows) {
		return null;
	}

	const statuses = rows.map((row) => { return row.status; });

	if (statuses.includes(VALIDITY_STATUS.EXPIRED)) {
		return VALIDITY_STATUS.EXPIRED;
	}

	if (statuses.includes(VALIDITY_STATUS.EXPIRING)) {
		return VALIDITY_STATUS.EXPIRING;
	}

	return null;
};
